import { useState, useRef, useEffect } from 'react'
import { Formik } from 'formik'
import { useSelector } from 'react-redux'

import { useUploadImage } from 'hooks'
import { useGetStickersQuery } from 'store/api/yay/misc'
import { getIsEternalImageMode } from 'store/actions'
import { CreateMessage } from 'lib/_types'

import ChatRoomModalSticker from 'components/Modal/ChatRoomModalSticker'
import ChatRoomModalGif from 'components/Modal/ChatRoomModalGif'
import Popup from 'components/Popup'
import Button from 'components/Button'

const MAX_MESSAGE_LENGTH = 255

type Props = {
  chatRoomId: number
  onSendMessage: (message: CreateMessage) => void
  replyMessageId?: number
  isGroupChat?: boolean
}

const ChatReplyForm = ({
  chatRoomId,
  onSendMessage,
  replyMessageId,
  isGroupChat = false,
}: Props) => {
  const { isImageValidForUpload } = useUploadImage()

  const [inputRows, setInputRows] = useState(1)

  const inputRef = useRef<HTMLTextAreaElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const isEternalImageMode = useSelector((state) =>
    getIsEternalImageMode(state)(chatRoomId)
  )

  const stickers = useGetStickersQuery()

  const onKeyDown = (e: any) => {
    if (e.keyCode === 229) {
      e.preventDefault()
    } else if (e.key.toLowerCase() === 'enter' && !e.shiftKey) {
      e.preventDefault()
      buttonRef.current.click()
    }
  }

  const validateInput = (values: any) => {
    if (!values?.text?.match?.(/\S+/g)) {
      return { text: 'input cannot be empty' }
    }
  }

  useEffect(() => {
    if (replyMessageId) inputRef.current.focus()
  }, [replyMessageId])

  return (
    <Formik
      initialValues={{ text: '' }}
      onSubmit={({ text }, { resetForm }) => {
        resetForm()
        onSendMessage({ messageType: 'text', text })
      }}
      validate={validateInput}
    >
      {({ values, handleChange, handleSubmit }) => {
        const onChange = (e: any) => {
          let rows = (e.target.value.match(/\n/g) || []).length + 1
          if (rows > 4) rows = 4

          setInputRows(rows)
          handleChange(e)
        }

        const _onSubmit = (e: any) => {
          handleSubmit(e)
          setInputRows(1)
        }

        return (
          <div className="ChatReplyForm">
            <form className="ChatReplyForm__form" onSubmit={_onSubmit}>
              <label className="ChatReplyForm__form__image">
                <input
                  onChange={async (e) => {
                    const file = e?.target?.files?.[0]

                    if (await isImageValidForUpload(file)) {
                      onSendMessage({
                        messageType:
                          isEternalImageMode || isGroupChat
                            ? 'eternal_image'
                            : 'image',
                        file,
                      })

                      e.target.value = null
                    }
                  }}
                  type="file"
                  accept=".jpg, .jpeg, .png, .gif"
                />
              </label>

              <Popup
                handleClass="sticker"
                disabled={(stickers?.data?.sticker_packs?.length ?? 0) === 0}
              >
                <ChatRoomModalSticker onSendMessage={onSendMessage} />
              </Popup>

              <Popup>
                <ChatRoomModalGif onSendMessage={onSendMessage} />
              </Popup>

              <textarea
                name="text"
                rows={inputRows}
                className="ChatReplyForm__form__input"
                maxLength={MAX_MESSAGE_LENGTH}
                onKeyDown={onKeyDown}
                onChange={onChange}
                value={values.text}
                ref={inputRef}
              />

              <Button
                ref={buttonRef}
                type="submit"
                icon="chat-send"
                buttonStyle="green"
                disabled={(values?.text?.trim?.().length || 0) == 0}
              />
            </form>
          </div>
        )
      }}
    </Formik>
  )
}

export default ChatReplyForm
